import { IEmailProviderConfiguration } from '@ViewModels';
import { StyleDeclarationValue, css } from 'aphrodite';
import { makeObservable, observable } from 'mobx';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { IUserSessionComponentProps, UserSessionViewModelKey } from '../../../../models/AppState';
import { IEventLoggingComponentProps, withEventLogging } from '../../../../models/Logging';
import { AutoDiscoverEmailViewModel } from '../../../../viewmodels/AdminViewModels';
import { LoadingSpinner } from '../../../../web/components/LoadingSpinner';
import { Modal } from '../../../../web/components/Modal';
import { Input } from '../../../../web/components-v2/Input';
import { styleSheet } from './styles';

interface IProps extends IUserSessionComponentProps, IEventLoggingComponentProps {
	className?: string;
	initialDomain?: string;
	onRequestClose?(config?: IEmailProviderConfiguration): void;
	showUseButton?: boolean;
	styles?: StyleDeclarationValue[];
}

class _AutoDiscoverEmailModal extends React.Component<IProps> {
	private mAutoDiscoverEmailViewModel: AutoDiscoverEmailViewModel;
	public constructor(props: IProps) {
		super(props);

		makeObservable<_AutoDiscoverEmailModal, 'mAutoDiscoverEmailViewModel'>(this, {
			mAutoDiscoverEmailViewModel: observable.ref,
		});

		this.mAutoDiscoverEmailViewModel = new AutoDiscoverEmailViewModel(props.userSession, props.initialDomain);
	}

	public render() {
		const { className, onRequestClose, styles } = this.props;
		return (
			<Modal
				className={`${css(styleSheet.container, ...(styles || []))} component-class ${className || ''}`}
				isOpen={true}
				onRequestClose={onRequestClose}
				useDefaultHeader={true}
			>
				<div>Auto Discover Email Configuration</div>
				<div className={css(styleSheet.inputRow)}>
					<Input
						styles={[styleSheet.textBox]}
						disabled={this.mAutoDiscoverEmailViewModel.isBusy}
						id='email'
						onChange={this.onChangeEmail}
						placeholder='Email Address'
						type='text'
						value={this.mAutoDiscoverEmailViewModel.email}
					/>
					<button className='cta-button-small' onClick={this.onGo} disabled={this.mAutoDiscoverEmailViewModel.isBusy}>
						Go
					</button>
				</div>
				{this.renderConfig()}
			</Modal>
		);
	}

	private renderConfig() {
		const { showUseButton } = this.props;
		if (this.mAutoDiscoverEmailViewModel.isBusy) {
			return <LoadingSpinner className={css(styleSheet.loading)} type='small' />;
		}

		if (this.mAutoDiscoverEmailViewModel.errorMessage) {
			return <div className={css(styleSheet.info)}>{this.mAutoDiscoverEmailViewModel.errorMessage}</div>;
		}

		const config = this.mAutoDiscoverEmailViewModel.emailProviderConfiguration;
		if (config) {
			return (
				<div>
					<div className={css(styleSheet.header)}>Email Provider</div>
					<div className={css(styleSheet.info)}>{config.emailProvider}</div>
					{!!config.imap?.imapHost && !!config.imap?.imapPort && (
						<>
							<div className={css(styleSheet.header)}>IMAP Configuration</div>
							<div className={css(styleSheet.info)}>{`${config.imap.imapHost} (${config.imap.imapPort})`}</div>
						</>
					)}
					{!!config.imap?.smtpHost && !!config.imap?.smtpPort && (
						<>
							<div className={css(styleSheet.header)}>SMTP Configuration</div>
							<div className={css(styleSheet.info)}>{`${config.imap.smtpHost} (${config.imap.smtpPort})`}</div>
						</>
					)}
					{!!config.exchange?.webServicesUrl && (
						<>
							<div className={css(styleSheet.header)}>Exchange Url</div>
							<div className={css(styleSheet.info)}>{config.exchange?.webServicesUrl}</div>
						</>
					)}
					{!!config.resolvedConfiguration?.emailSendLimits?.length && (
						<>
							<div className={css(styleSheet.header)}>Send Limits</div>
							{config.resolvedConfiguration.emailSendLimits.map((sendLimit, index) => (
								<div key={index.toString()} className={css(styleSheet.info)}>{`${
									sendLimit.count
								} per ${this.getIntervalText(sendLimit.intervalInMinutes)}`}</div>
							))}
						</>
					)}

					{config.resolvedConfiguration?.secondsBetweenSends > 0 && (
						<>
							<div className={css(styleSheet.header)}>Seconds Between Sends</div>

							<div className={css(styleSheet.info)}>{config.resolvedConfiguration.secondsBetweenSends}</div>
						</>
					)}

					{config.resolvedConfiguration?.maxMessageSizeInBytes > 0 && (
						<>
							<div className={css(styleSheet.header)}>Max Message Size</div>
							<div className={css(styleSheet.info)}>{`${this.getRounded(
								config.resolvedConfiguration.maxMessageSizeInBytes
							)} MB`}</div>
						</>
					)}
					{showUseButton && (
						<button
							className={`cta-button-small ${css(styleSheet.useButton)}`}
							onClick={this.onUseSettings}
							disabled={this.mAutoDiscoverEmailViewModel.isBusy}
						>
							Use This Configuration
						</button>
					)}
				</div>
			);
		}
	}

	private getRounded(maxMessageSizeInBytes: number) {
		const maxMessageSizeInMegaBytes = maxMessageSizeInBytes / 1000000;
		return Math.round((maxMessageSizeInMegaBytes + Number.EPSILON) * 100) / 100;
	}

	private getIntervalText(intervalInMinutes: number) {
		switch (intervalInMinutes) {
			case 1440:
				return 'day';
			case 60:
				return 'hour';
			default:
				return `${intervalInMinutes} minutes`;
		}
	}

	private onGo = () => {
		const { logApiError, logEvent } = this.props;

		logEvent('LoadSuggestedEmailProvider');
		this.mAutoDiscoverEmailViewModel.loadSuggestedEmailProvider()?.catch(error => {
			logApiError('LoadSuggestedEmailProvider-Error', error);
		});
	};

	private onUseSettings = () => {
		this.props.onRequestClose?.(this.mAutoDiscoverEmailViewModel.emailProviderConfiguration);
	};

	private onChangeEmail = (event: React.ChangeEvent<HTMLInputElement>) =>
		(this.mAutoDiscoverEmailViewModel.email = event.target.value);
}

const AutoDiscoverEmailModalAsObserver = observer(_AutoDiscoverEmailModal);
const AutoDiscoverEmailModalWithContext = inject(UserSessionViewModelKey)(AutoDiscoverEmailModalAsObserver);
export const AutoDiscoverEmailModal = withEventLogging(AutoDiscoverEmailModalWithContext, 'AutoDiscoverEmailModal');
